import { useState } from "react";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";
import { useAuth } from "../../context/AuthContext";

const FALLBACK_CODE = "SURPRISE25"; // Default referral code if user data is not available

const ReferralInfo = () => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const { user } = useAuth();

  // Generate a referral code based on username (or fallback if not available)
  const referralCode = user?.username ?
    `${user.username.toUpperCase()}${new Date().getFullYear()}` :
    FALLBACK_CODE;

  const handleCopyReferral = () => {
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    toast({
      title: "Copied!",
      description: "Referral code copied to clipboard",
    });

    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = (platform: string) => {
    const referralLink = `${window.location.origin}/register?ref=${referralCode}`;

    let shareUrl = "";
    switch (platform) {
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`;
        break;
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          "Join SurpriseTable and get €5 off your first booking! Use my referral code:"
        )}&url=${encodeURIComponent(referralLink)}`;
        break;
      case "whatsapp":
        shareUrl = `https://wa.me/?text=${encodeURIComponent(
          `Join SurpriseTable and get €5 off your first booking! Use my referral code: ${referralCode}. ${referralLink}`
        )}`;
        break;
      case "email":
        shareUrl = `mailto:?subject=${encodeURIComponent(
          "Discover Surprise Dining with SurpriseTable"
        )}&body=${encodeURIComponent(
          `Join SurpriseTable and get €5 off your first booking! Use my referral code: ${referralCode}. ${referralLink}`
        )}`;
        break;
    }

    if (shareUrl) {
      window.open(shareUrl, "_blank");
    }
  };

  return (
    <div className="md:w-1/2 p-8 md:p-12">
      <h3 className="mb-6 text-2xl font-bold text-gray-800 dark:text-white">
        How Referrals Work
      </h3>

      <div className="space-y-6">
        <div className="flex items-start">
          <div className="mr-4 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
            <span className="font-bold text-primary dark:text-primary-dark">
              1
            </span>
          </div>
          <div>
            <h4 className="mb-1 font-medium text-gray-800 dark:text-white">
              Share Your Code
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Send your unique referral code to friends who love dining
              experiences.
            </p>
          </div>
        </div>

        <div className="flex items-start">
          <div className="mr-4 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
            <span className="font-bold text-primary dark:text-primary-dark">
              2
            </span>
          </div>
          <div>
            <h4 className="mb-1 font-medium text-gray-800 dark:text-white">
              Friend Signs Up
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              When they register using your code, they get €5 off their first
              booking.
            </p>
          </div>
        </div>

        <div className="flex items-start">
          <div className="mr-4 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
            <span className="font-bold text-primary dark:text-primary-dark">
              3
            </span>
          </div>
          <div>
            <h4 className="mb-1 font-medium text-gray-800 dark:text-white">
              You Get Rewarded
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              After their first successful booking, you receive €10 credit for
              your next surprise.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralInfo;
